import { useRefAsState } from 'modules/React.Extensions';
import { useDispatch, useSelector } from 'react-redux';
import { authFetch } from 'api/auth/authFetch';
import { transformDraftToProgrammeInfo, transformEpisodeDraftToEpisodeInfo } from 'modules/data-entry/AudioVisual/dataTransforms';
import { setProgrammeUpdateAuditDataAction, setEpisodeUpdateAuditDataAction } from 'components/Body/data-entry/AudioVisual/store/actions';
import { isFilmByFormatId } from '../../../modules/Constants';

const updateDataByEndpoint = async (endpoint, body, original, episodeDraft, episodeOriginal, aggregateVersion, controller, setNewAggregate, isEpisodeUpdate = false) => {
    let responseCode = -1

    try {
        const transformedBody = isEpisodeUpdate
            ? transformEpisodeDraftToEpisodeInfo(episodeOriginal, episodeDraft)
            : transformDraftToProgrammeInfo(body, original, episodeOriginal, episodeDraft, false)

        const response = await authFetch(endpoint, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'AggregateVersion': aggregateVersion
            },
            body: JSON.stringify(transformedBody),
            signal: controller.signal,
        });

        if (controller.signal.aborted) return null

        responseCode = response.status

        if (responseCode === 200) {
            const aggregateVersionResponse = response.headers.get("aggregateversion")
            setNewAggregate(aggregateVersionResponse)
            return { responseCode }
        }

        const errorText = await response.text()
        throw {
            Message: 'The AV data update was not successful',
            StatusCode: responseCode,
            Details: errorText,
            ErrorMessage: errorText
        };
    } catch (error) {
        if (error.StatusCode) {
            // Re-throw errors we've already formatted
            throw error
        }
        throw {
            Message: 'The AV data update was not successful',
            StatusCode: responseCode,
            Details: error,
            ErrorMessage: error.message || 'Network error occurred'
        };
    }
};

const setUpUpdate = (endpoint, body, original, episodeDraft, episodeOriginal, aggregateVersion, setNewAggregate, isEpisodeUpdate = false) => {
    const controller = new AbortController()
    return {
        controller,
        update: () => updateDataByEndpoint(endpoint, body, original, episodeDraft, episodeOriginal, aggregateVersion, controller, setNewAggregate, isEpisodeUpdate),
    }
}

const DataUpdate = (props) => {
    const [abortController, setAbortController] = useRefAsState(null)
    const { setStatusAction, requestSelector, onComplete, setAggregateAction, episodeOnly } = props

    const dispatch = useDispatch();
    const setStatus = (newStatus) => dispatch(setStatusAction(newStatus))
    const setNewAggregate = (newAggregate) => dispatch(setAggregateAction(newAggregate))
    const setUserNameAndDate = (newUpdateDate, newUserName) => dispatch(setProgrammeUpdateAuditDataAction(newUpdateDate, newUserName))
    const setEpisodeUserNameAndDate = (newUpdateDate, newUserName) => dispatch(setEpisodeUpdateAuditDataAction(newUpdateDate, newUserName))

    const dataUpdateRequest = useSelector(requestSelector)
    const { endpoint, status, body, original, episodeDraft, episodeOriginal, aggregateVersion, userName } = dataUpdateRequest

    const handleUpdate = async () => {
        const statusKey = episodeOnly ? 'updateStatus' : 'status'
        setStatus({ [statusKey]: 'pending' })

        const { controller, update } = setUpUpdate(endpoint, body, original, episodeDraft, episodeOriginal, aggregateVersion, setNewAggregate, episodeOnly)
        setAbortController(controller)

        try {
            const result = await update()
            if (result) {
                const currentDate = new Date()
                if (!episodeOnly) setUserNameAndDate(currentDate, userName)
                //For Film Programmes, we will update programme and episode details in one go so need to update both audit fields
                if (episodeOnly || (episodeDraft !== undefined && isFilmByFormatId(original.formatId))) setEpisodeUserNameAndDate(currentDate, userName)        
                setStatus({ [statusKey]: 'complete' })
                if (onComplete) setTimeout(onComplete, 5000)
            }
        } catch (error) {
            setStatus({ [statusKey]: 'error', error: error })
        }
    };

    if (status.updateStatus === 'idle' || status === 'idle') handleUpdate()

    return null
};

export default DataUpdate
